/**
 * Clean up old localStorage keys after migration to IndexedDB
 *
 * This utility removes the old 'reedle-articles' and 'reedle-reading-progress'
 * localStorage keys to free up space (~5MB for heavy users).
 *
 * Runs once after successful app load, then sets a completion flag.
 * Safe to call multiple times (idempotent).
 */

const CLEANUP_FLAG = 'reedle-storage-cleanup-v1';
const OLD_ARTICLES_KEY = 'reedle-articles';
const OLD_PROGRESS_KEY = 'reedle-reading-progress';

/**
 * Clean up old localStorage keys
 * Call this after app successfully loads with IndexedDB
 */
export function cleanupOldStorage() {
  // Check if cleanup already done
  if (localStorage.getItem(CLEANUP_FLAG) === 'true') {
    return;
  }

  try {
    // Check if old keys exist
    const hasOldArticles = localStorage.getItem(OLD_ARTICLES_KEY) !== null;
    const hasOldProgress = localStorage.getItem(OLD_PROGRESS_KEY) !== null;

    if (hasOldArticles || hasOldProgress) {
      console.log('[Storage Cleanup] Removing old localStorage keys...');

      // Delete old keys
      if (hasOldArticles) {
        localStorage.removeItem(OLD_ARTICLES_KEY);
        console.log(`[Storage Cleanup] Removed '${OLD_ARTICLES_KEY}'`);
      }

      if (hasOldProgress) {
        localStorage.removeItem(OLD_PROGRESS_KEY);
        console.log(`[Storage Cleanup] Removed '${OLD_PROGRESS_KEY}'`);
      }

      console.log('[Storage Cleanup] Cleanup complete!');
    } else {
      console.log('[Storage Cleanup] No old keys found, skipping cleanup');
    }

    // Mark cleanup as complete (whether keys existed or not)
    localStorage.setItem(CLEANUP_FLAG, 'true');
  } catch (error) {
    // Non-critical error - log and continue
    console.warn('[Storage Cleanup] Failed to clean up old storage:', error);

    // Still mark as complete to prevent retry loops
    try {
      localStorage.setItem(CLEANUP_FLAG, 'true');
    } catch (flagError) {
      // Can't even set flag - localStorage might be full or disabled
      console.error('[Storage Cleanup] Cannot set cleanup flag:', flagError);
    }
  }
}
